import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScribeGuide, ScribeGuideProps } from './ScribeGuide';
import { Search, Filter, Loader2, Bookmark, BookmarkCheck, ExternalLink, Maximize2, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScribeGuideService, ScribeGuide as ScribeGuideType } from '@/services/scribeGuideService';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from 'sonner';

export interface ScribeManagerProps {
  className?: string;
  showSearch?: boolean;
  defaultCategory?: string;
}

export const ScribeManager: React.FC<ScribeManagerProps> = ({
  className,
  showSearch = true,
  defaultCategory
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(defaultCategory || 'all');
  const [expandedGuide, setExpandedGuide] = useState<string | null>(null);
  const [guides, setGuides] = useState<ScribeGuideType[]>([]);
  const [categories, setCategories] = useState<string[]>(['all']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookmarkedGuides, setBookmarkedGuides] = useState<Set<string>>(new Set());
  const [bookmarkLoading, setBookmarkLoading] = useState<Set<string>>(new Set());

  const { user } = useAuth();
  const { organization } = useOrganization();

  // Fetch guides and bookmarks from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [guidesData, categoriesData] = await Promise.all([
          ScribeGuideService.getGuides(organization?.id),
          ScribeGuideService.getCategories(organization?.id)
        ]);

        setGuides(guidesData);
        setCategories(['all', ...categoriesData]);

        // Fetch user bookmarks if user is logged in
        if (user?.id) {
          const bookmarkedGuidesData = await ScribeGuideService.getUserBookmarks(user.id, organization?.id);
          const bookmarkedIds = new Set(bookmarkedGuidesData.map(guide => guide.id));
          setBookmarkedGuides(bookmarkedIds);
        }
      } catch (err) {
        console.error('Error fetching guides:', err);
        setError('Failed to load guides. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization?.id, user?.id]);

  // Handle bookmark toggle
  const handleBookmarkToggle = async (guideId: string) => {
    if (!user?.id) return;

    setBookmarkLoading(prev => new Set(prev).add(guideId));
    try {
      const isBookmarked = bookmarkedGuides.has(guideId);
      
      if (isBookmarked) {
        await ScribeGuideService.unbookmarkGuide(guideId, user.id);
        setBookmarkedGuides(prev => {
          const newSet = new Set(prev);
          newSet.delete(guideId);
          return newSet;
        });
        toast.success('Removed from bookmarks');
      } else {
        await ScribeGuideService.bookmarkGuide(guideId, user.id);
        setBookmarkedGuides(prev => new Set(prev).add(guideId));
        toast.success('Added to bookmarks');
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      toast.error('Failed to update bookmark');
    } finally {
      setBookmarkLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(guideId);
        return newSet;
      });
    }
  };

  // Filter guides based on search and category
  const filteredGuides = guides.filter(guide => {
    const matchesSearch = !searchTerm || 
      guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guide.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guide.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || guide.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Group guides by category for display
  const groupedGuides = categories.reduce((acc, category) => {
    const categoryGuides = filteredGuides.filter(guide => 
      category === 'all' ? true : guide.category === category
    );
    if (categoryGuides.length > 0) {
      acc[category] = categoryGuides;
    }
    return acc;
  }, {} as Record<string, ScribeGuideType[]>);

  const handleGuideClick = (guideId: string) => {
    setExpandedGuide(expandedGuide === guideId ? null : guideId);
  };


  // Loading state
  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        <Card>
          <CardContent className="py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
            <p className="text-sm text-gray-600">Loading interactive guides...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn("space-y-6", className)}>
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Search and Controls */}
      {showSearch && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search guides..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${categories.length}, 1fr)` }}>
          {categories.map(category => (
            <TabsTrigger
              key={category}
              value={category}
              className="capitalize"
            >
              {category === 'all' ? 'All Guides' : category}
            </TabsTrigger>
          ))}
        </TabsList>

        {categories.map(category => (
          <TabsContent key={category} value={category} className="space-y-4">
            {groupedGuides[category]?.map(guide => (
              <Card key={guide.id} className="transition-all duration-200 hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">{guide.title}</CardTitle>
                      {guide.description && (
                        <CardDescription className="mt-1 text-sm">
                          {guide.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {guide.is_featured && (
                        <Badge className="bg-primary text-primary-foreground">
                          Featured
                        </Badge>
                      )}
                      {user && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleBookmarkToggle(guide.id)}
                          disabled={bookmarkLoading.has(guide.id)}
                          className="h-8 w-8 p-0"
                          title={bookmarkedGuides.has(guide.id) ? 'Remove from bookmarks' : 'Add to bookmarks'}
                        >
                          {bookmarkLoading.has(guide.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : bookmarkedGuides.has(guide.id) ? (
                            <BookmarkCheck className="h-4 w-4 text-primary" />
                          ) : (
                            <Bookmark className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                        title="Open in new tab"
                      >
                        <a href={guide.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        variant={expandedGuide === guide.id ? "secondary" : "default"}
                        size="sm"
                        onClick={() => handleGuideClick(guide.id)}
                        className={cn(
                          "font-semibold transition-all duration-200 shadow-sm",
                          expandedGuide === guide.id 
                            ? "bg-gray-100 hover:bg-gray-200 text-gray-700" 
                            : "bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80 text-white hover:shadow-md transform hover:scale-105"
                        )}
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        {expandedGuide === guide.id ? 'Hide Guide' : 'Show Guide'}
                      </Button>
                    </div>
                  </div>
                  {guide.tags && guide.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {guide.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>

                {expandedGuide === guide.id && (
                  <CardContent className="pt-0">
                    <ScribeGuide
                      url={guide.url}
                      title={guide.title}
                      description={guide.description}
                      category={guide.category}
                      showHeader={false}
                      allowFullscreen={false}
                      height={500}
                      guideId={guide.id}
                      showBookmark={false}
                    />
                  </CardContent>
                )}
              </Card>
            ))}

            {(!groupedGuides[category] || groupedGuides[category].length === 0) && (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-gray-500">No guides found for this category.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ScribeManager;